import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, StatusLight, ActionButton, TooltipTrigger, Tooltip } from "@react-spectrum/s2";
import type { LoadingState, SortDescriptor } from '@react-types/shared';
import { OpenIn } from '../icons/s2';
import { CCOrganization } from '../../types';
import { formatDate } from '../../utils/dateUtils';
import { ColumnDefinition } from '../common/DataTable';
import { ActionsColumnHeader } from '../common/ActionsColumnHeader';
import { FilterableTable } from '../common/FilterableTable';
import type { FiltersState } from '../common/FilterableTable';
import { UsageProgressBar } from '../common/UsageProgressBar';
import { formatUsageLabel, calcPercentage } from './utils';
import '../ManageUsers/ManageUsersPanel.css';
interface OrganizationsTableProps {
    organizations: CCOrganization[];
    loading: boolean;
    loadingState?: LoadingState;
    onManageOrg: (org: CCOrganization) => void;
    sortDescriptor?: SortDescriptor;
    onSortChange?: (descriptor: SortDescriptor) => void;
    onLoadMore?: () => void;
    filterValues: FiltersState;
    onFiltersChange: (filters: FiltersState) => void;
    onFiltersClear: () => void;
    hasError?: boolean;
}
export const OrganizationsTable: React.FC<OrganizationsTableProps> = ({ organizations, loading, loadingState, onManageOrg, sortDescriptor, onSortChange, onLoadMore, filterValues, onFiltersChange, onFiltersClear, hasError = false })=>{
    const { t } = useTranslation();
    const columns: ColumnDefinition<CCOrganization>[] = useMemo(()=>[
            {
                key: 'org_id',
                label: t('commandCenter.organizationsTable.columns.id'),
                minWidth: 100,
                defaultVisible: false,
                allowsSorting: true,
                allowsResizing: true,
                align: 'start',
                render: (org)=>(<span className="cc-cell-org-id" title={org.org_id}>{org.org_id}</span>)
            },
            {
                key: 'org_name',
                label: t('commandCenter.organizationsTable.columns.name'),
                minWidth: 160,
                allowsSorting: true,
                isRowHeader: true,
                allowsResizing: true,
                pinned: true,
                align: 'start',
                render: (org)=>(<span className="cc-cell-org-name" title={org.org_name}>{org.org_name}</span>)
            },
            {
                key: 'created_by',
                label: t('commandCenter.organizationsTable.columns.createdBy'),
                minWidth: 140,
                allowsSorting: true,
                allowsResizing: true,
                align: 'start',
                render: (org)=>(<span className="cc-creator-name" title={org.created_by || t('commandCenter.organizationsTable.unknown')}>{org.created_by || t('commandCenter.organizationsTable.unknown')}</span>),
                defaultVisible: false
            },
            {
                key: 'domain',
                label: t('commandCenter.organizationsTable.columns.domains'),
                minWidth: 100,
                allowsSorting: true,
                allowsResizing: true,
                render: (org)=>(<Text UNSAFE_className="cc-cell-number">{org.domain?.used?.toLocaleString() || 0}</Text>)
            },
            {
                // No `align: 'center'` — StatusLight's dot+gap chrome renders wider
                // than the plain header label, so centering each independently
                // (how Spectrum's Column/Cell alignment works) left-shifts the dot
                // relative to the header text; `start` keeps both flush left. Same
                // fix as the Connected/Deprecated columns in the model tables.
                key: 'type',
                label: t('commandCenter.organizationsTable.columns.type'),
                minWidth: 140,
                allowsSorting: true,
                allowsResizing: true,
                render: (org)=>(<StatusLight variant={org.type === 'SANDBOX' ? 'informative' : 'positive'}>
            {org.type === 'SANDBOX' ? t('commandCenter.organizationsTable.sandbox') : t('commandCenter.organizationsTable.provisioned')}
          </StatusLight>)
            },
            {
                key: 'request_usage',
                label: t('commandCenter.organizationsTable.columns.queries'),
                minWidth: 180,
                allowsSorting: true,
                align: 'start',
                allowsResizing: true,
                render: (org)=>{
                    const used = org.request_usage?.used || 0;
                    const total = org.request_usage?.total || 0;
                    const percentage = calcPercentage(used, total);
                    return (<div className={"cc-cell-usage" + " sd171 eb171 Ue171 qe171"}>
              <Text UNSAFE_className="cc-usage-percentage">{percentage}%</Text>
              <UsageProgressBar used={used} total={total} label={formatUsageLabel(used, total)} showLabel/>
            </div>);
                }
            },
            {
                key: 'document_indexed',
                label: t('commandCenter.organizationsTable.columns.indexedFiles'),
                minWidth: 140,
                allowsSorting: true,
                align: 'start',
                allowsResizing: true,
                render: (org)=>{
                    const indexed = org.document_indexed?.used || 0;
                    const total = org.document_indexed?.total || 0;
                    const percentage = calcPercentage(indexed, total);
                    return (<div className={"cc-cell-usage" + " sd171 eb171 Ue171 qe171"}>
              <Text UNSAFE_className="cc-usage-percentage">{percentage}%</Text>
              <UsageProgressBar used={indexed} total={total} label={formatUsageLabel(indexed, total)} showLabel/>
            </div>);
                }
            },
            {
                key: 'subscription_end',
                label: t('commandCenter.organizationsTable.columns.endDate'),
                minWidth: 140,
                allowsSorting: true,
                allowsResizing: true,
                render: (org)=>(<Text UNSAFE_className="cc-cell-date">
            {org.subscription_end ? formatDate(org.subscription_end) : '—'}
          </Text>)
            },
            {
                // Single action, so render it directly instead of behind a kebab menu.
                key: 'actions',
                label: t('commandCenter.organizationsTable.columns.actions'),
                width: 72,
                excludeFromConfigurator: true,
                renderHeader: ()=><ActionsColumnHeader/>,
                render: (org)=>(<TooltipTrigger delay={300}>
            <ActionButton isQuiet aria-label={t('commandCenter.organizationsTable.manageAria', {
                        name: org.org_name
                    })} onPress={()=>onManageOrg(org)}>
              <OpenIn size="S"/>
            </ActionButton>
            <Tooltip>{t('commandCenter.organizationsTable.manage')}</Tooltip>
          </TooltipTrigger>)
            }
        ], [
        onManageOrg,
        t
    ]);
    const filterConfigs = useMemo(()=>[
            {
                key: 'org_id',
                label: t('commandCenter.organizationsTable.filters.id'),
                placeholder: t('commandCenter.organizationsTable.searchById')
            },
            {
                key: 'org_name',
                label: t('commandCenter.organizationsTable.filters.name'),
                placeholder: t('commandCenter.organizationsTable.searchByName')
            },
            {
                key: 'created_by',
                label: t('commandCenter.organizationsTable.filters.createdBy'),
                placeholder: t('commandCenter.organizationsTable.searchByUser')
            },
            {
                key: 'domain',
                label: t('commandCenter.organizationsTable.filters.domains'),
                type: 'range_number' as const
            },
            {
                key: 'subscription_end',
                label: t('commandCenter.organizationsTable.filters.endDate'),
                type: 'range_date' as const
            }
        ], [
        t
    ]);
    return (<FilterableTable<CCOrganization> filterConfigs={filterConfigs} filterValues={filterValues} onFiltersChange={onFiltersChange} onFiltersClear={onFiltersClear} items={organizations} columns={columns} getRowKey={(org)=>org.org_id} loading={loading} loadingState={loadingState} ariaLabel={t('commandCenter.organizationsTable.ariaLabel')} sortDescriptor={sortDescriptor} onSortChange={onSortChange} onLoadMore={onLoadMore} emptyStateTitle={t('commandCenter.organizationsTable.emptyTitle')} emptyStateDescription={t('commandCenter.organizationsTable.emptyDescription')} hasError={hasError} errorStateTitle={t('commandCenter.organizationsTable.errorTitle')} errorStateDescription={t('commandCenter.organizationsTable.errorDescription')} configurableColumns layout="fill"/>);
};

import "macro-670689c431bc3bbe3dd9c9217c99a533309a8e20cb86cb029d05484a86ca10f4.css";
import "macro-670689c431bc3bbe3dd9c9217c99a533309a8e20cb86cb029d05484a86ca10f4.css";